import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { logException } from 'App/logging';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import { VerifyUserForRegistrationByMembershipIdOutcome as Outcome } from '__generated__/globalTypes';
import { TrackedFailureReason, TrackedOutcome } from '../constants';
import { VerificationTracker } from '../types';
import {
  VerifyUserForRegistrationByMembershipId,
  VerifyUserForRegistrationByMembershipIdVariables,
  VerifyUserForRegistrationByMembershipId_verifyUserForRegistrationByMembershipId_displayEmail,
} from './__generated__/VerifyUserForRegistrationByMembershipId';

export const VERIFY_USER_FOR_REGISTRATION_BY_MEMBERSHIP_ID = gql`
  mutation VerifyUserForRegistrationByMembershipId(
    $input: VerifyUserForRegistrationByMembershipIdInput!
  ) {
    verifyUserForRegistrationByMembershipId(input: $input) {
      outcome
      displayEmail {
        value
        masked
      }
    }
  }
`;

export type DisplayEmail =
  VerifyUserForRegistrationByMembershipId_verifyUserForRegistrationByMembershipId_displayEmail;

interface UseVerifyUserForRegistrationByMembershipId {
  tracker: VerificationTracker;
  onVerificationEmailSent(displayEmail: DisplayEmail): void;
  onSignInLinkSent(displayEmail: DisplayEmail): void;
  onInvalidLink(): void;
  onError(message: string): void;
}

export const useVerifyUserForRegistrationByMembershipId = ({
  tracker,
  onVerificationEmailSent,
  onSignInLinkSent,
  onInvalidLink,
  onError,
}: UseVerifyUserForRegistrationByMembershipId) => {
  const { t: translate } = useTranslation('shared');
  const genericErrorMessage = translate('errors.messages.failed');

  const handleError = (error: Error) => {
    tracker.failed(error);
    logException(error);
    onError(genericErrorMessage);
  };

  const [verifyUserForRegistrationByMembershipId, { loading }] = useMutation<
    VerifyUserForRegistrationByMembershipId,
    VerifyUserForRegistrationByMembershipIdVariables
  >(VERIFY_USER_FOR_REGISTRATION_BY_MEMBERSHIP_ID, {
    onCompleted: data => {
      const { outcome, displayEmail } =
        data.verifyUserForRegistrationByMembershipId;

      if (outcome === Outcome.INVALID_LINK) {
        tracker.failed(TrackedFailureReason.InvalidLink);

        logException(
          new Error(
            'verifyUserForRegistrationByMembershipId returned INVALID_LINK',
          ),
          { tags: { subdomain: getSubdomainFromUrl() } },
        );

        return onInvalidLink();
      }

      if (!displayEmail) {
        const missingDisplayEmailError = new Error(
          `verifyUserForRegistrationByMembershipId returned ${outcome} without a displayEmail`,
        );

        return handleError(missingDisplayEmailError);
      }

      if (outcome === Outcome.VERIFICATION_EMAIL_SENT) {
        tracker.attempted(TrackedOutcome.SignUpLinkSent);

        return onVerificationEmailSent(displayEmail);
      }

      if (outcome === Outcome.SIGN_IN_LINK_SENT) {
        tracker.attempted(TrackedOutcome.SignInLinkSent);

        return onSignInLinkSent(displayEmail);
      }

      const unexpectedOutcomeError = new Error(
        `verifyUserForRegistrationByMembershipId returned unexpected outcome ${outcome}`,
      );

      return handleError(unexpectedOutcomeError);
    },
    onError: handleError,
  });

  const verifyByMembershipId = async (membershipId: string) => {
    await verifyUserForRegistrationByMembershipId({
      variables: { input: { membershipId } },
    });
  };

  return {
    verifyByMembershipId,
    loading,
  };
};
